"use client";
import { useState, useEffect, useCallback } from 'react'

// Import translation files
import enCommon from '../../locales/en/common.json'
import arCommon from '../../locales/ar/common.json'

export type Locale = 'en' | 'ar'

export interface TranslationNamespaces {
  common: typeof enCommon
}

// Translation resources
const resources = {
  en: {
    common: enCommon,
  },
  ar: {
    common: arCommon,
  },
} as const

// Get nested value from object using dot notation
function getNestedValue(obj: any, path: string): string {
  return path.split('.').reduce((current, key) => current?.[key], obj) || path
}

// Replace placeholders in translation strings
function interpolate(template: string, values: Record<string, string | number> = {}): string {
  if (typeof template !== 'string') return template;
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return values[key]?.toString() || match
  })
}

// Translation function
export function t(
  key: string,
  namespace: keyof TranslationNamespaces = 'common',
  locale: Locale = 'en',
  values?: Record<string, string | number>
): string {
  const translation = getNestedValue(resources[locale][namespace], key)
  return values ? interpolate(translation, values) : translation
}

// Global locale state management
let globalLocale: Locale = 'en'
const localeListeners: Set<(locale: Locale) => void> = new Set()

// Initialize global locale from localStorage when available
if (typeof window !== 'undefined') {
  const savedLanguage = localStorage.getItem('preferred-language') as Locale
  globalLocale = savedLanguage || 'en'
  
  // Set HTML attributes on initialization
  document.documentElement.lang = globalLocale
  document.documentElement.dir = globalLocale === 'ar' ? 'rtl' : 'ltr'
}

function setGlobalLocale(locale: Locale) {
  globalLocale = locale
  
  if (typeof window !== 'undefined') {
    localStorage.setItem('preferred-language', locale)
    document.documentElement.lang = locale
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr'
  }
  
  localeListeners.forEach(listener => listener(locale))
}

function subscribeToLocaleChanges(listener: (locale: Locale) => void) {
  localeListeners.add(listener)
  return () => { localeListeners.delete(listener) }
}

// Hook for translations
export function useTranslation(namespace: keyof TranslationNamespaces = 'common') {
  const [locale, setLocale] = useState<Locale>(globalLocale)

  useEffect(() => {
    const unsubscribe = subscribeToLocaleChanges(setLocale)
    return unsubscribe
  }, [])

  const translate = (key: string, values?: Record<string, string | number>) => {
    return t(key, namespace, locale, values)
  }

  return {
    t: translate,
    locale,
    isRTL: locale === 'ar',
  }
}

// Hook for language switching
export function useLanguage() {
  const [locale, setLocaleState] = useState<Locale>(globalLocale)

  useEffect(() => {
    const unsubscribe = subscribeToLocaleChanges(setLocaleState)
    return unsubscribe
  }, [])

  const changeLanguage = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale)
    setGlobalLocale(newLocale)
  }, [])

  const isRTL = locale === 'ar'

  return {
    locale,
    changeLanguage,
    isRTL,
    availableLanguages: [
      { code: 'en', name: 'English', nativeName: 'English' },
      { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
    ] as const,
  }
}

// Initialize language on app start
export function initializeLanguage() {
  if (typeof window !== 'undefined') {
    const savedLanguage = localStorage.getItem('preferred-language') as Locale
    const currentLocale = savedLanguage || 'en'
    
    setGlobalLocale(currentLocale)
    return currentLocale
  }
  return 'en'
}

// Format numbers based on locale
export function formatNumber(
  number: number,
  locale: Locale = 'en',
  options?: Intl.NumberFormatOptions
): string {
  const localeMap = {
    en: 'en-US',
    ar: 'ar-SA',
  }
  
  return new Intl.NumberFormat(localeMap[locale], options).format(number)
}

// Format dates based on locale
export function formatDate(
  date: Date | string,
  locale: Locale = 'en',
  options?: Intl.DateTimeFormatOptions
): string {
  const localeMap = {
    en: 'en-US',
    ar: 'ar-SA',
  }
  
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat(localeMap[locale], options).format(dateObj)
}

export default {
  t,
  useTranslation,
  useLanguage,
  initializeLanguage,
  formatNumber,
  formatDate,
}
